import { InjectionToken } from '@angular/core';
import { FormlyField, FormlyFieldConfig } from '@ngx-formly/core';
import { defaultsDeep, get, map } from 'lodash';

export type FlexibleFormFieldsDefinition = Record<string, FormlyFieldConfig>;

export type FlexibleFormFieldsVariants = {
  DEFAULT: FormlyFieldConfig[];
} & Record<number | string, FormlyFieldConfig[]>;

export type FlexibleFormInitializers = {
  DEFAULT: (model: any, ...args: any) => any;
} & Record<number | string, (model: any, ...args: any) => any>;

export type FlexibleFormParsers = {
  DEFAULT: (model: any, ...args: any) => any;
} & Record<number | string, (model: any, ...args: any) => any>;

export interface FlexibleFormsConfig {
  definition: FlexibleFormFieldsDefinition;
  variants: FlexibleFormFieldsVariants;
  initializers?: FlexibleFormInitializers;
  parsers?: FlexibleFormParsers;
}

export const FLEXIBLE_FORMS_CONFIG = new InjectionToken<FlexibleFormsConfig>(
  'Field builder definition'
);

export function provideFlexibleForms(config: FlexibleFormsConfig) {
  return { provide: FLEXIBLE_FORMS_CONFIG, useValue: config };
}

export function provideFactoryFlexibleForms<TArgs, TDeps>(
  useFactory: (...args: TArgs[]) => FlexibleFormsConfig,
  deps: TDeps
) {
  return { provide: FLEXIBLE_FORMS_CONFIG, useFactory, deps };
}

export function buildFields(
  definition: FlexibleFormFieldsDefinition,
  fields: FormlyFieldConfig[],
  model: unknown = {}
): FormlyFieldConfig[] {
  return map(fields, (field) => {
    if (field.fieldGroup) {
      return {
        ...field,
        fieldGroup: buildFields(definition, field.fieldGroup, model),
      } as FormlyFieldConfig;
    }

    return defaultsDeep(
      { defaultValue: get(model, field.id as string, field.defaultValue) },
      field,
      get(definition, field.id as string)
    ) as FormlyFieldConfig;
  });
}

export function buildField(field: FormlyField, defaultField: FormlyField) {
  return defaultsDeep(field, defaultField);
}
